import React, {useEffect, useMemo, useState} from "react";
import { BACKEND_URL } from "../config";
import "./HabitsView.css";

const todayISO = () => new Date().toISOString().slice(0, 10);

// NOTE: labels say Mon..Sun as 0..6.
// Keep this consistent with backend expectations.
const weekLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // 0=Mon..6=Sun

function safeNum(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function compact(obj) {
  // remove undefined, null, and empty-string fields
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}

function HabitBadge({ children, bg = "#111827" }) {
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color: "white",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {children}
    </span>
  );
}

export default function HabitsView({ token, authUser, adminUsers, showToast }) {

// Batch 6: Habit adherence summary
const [adherenceDays, setAdherenceDays] = useState(30);
const [adherence, setAdherence] = useState({ pct: null, completed: 0, total: 0, streak: 0, daily: [] });
const [adherenceLoading, setAdherenceLoading] = useState(false);

const loadAdherence = async (days) => {
  if (!token) return;
  const d = Math.max(1, Math.min(Number(days || 30), 3650));
  setAdherenceLoading(true);
  try {
    // Habits view should use the normal habits list endpoint and aggregate
    // using the real per-habit /progress route.
    const habitsRes = await fetch(`${BACKEND_URL}/api/habits?user_id=me`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!habitsRes.ok) throw new Error(await habitsRes.text());

    const habitsData = await habitsRes.json();
    const habitsList = Array.isArray(habitsData?.habits) ? habitsData.habits : [];

    let total = 0;
    let completed = 0;

    await Promise.all(
      habitsList
        .filter((h) => h?.habit_id)
        .map(async (h) => {
          try {
            const res = await fetch(
              `${BACKEND_URL}/api/habits/${encodeURIComponent(h.habit_id)}/progress?user_id=me&days=${d}`,
              {
                headers: { Authorization: `Bearer ${token}` },
                cache: "no-store",
              }
            );
            if (!res.ok) return;
            const prog = await res.json();
            completed += Number(prog?.completed_count || 0);
            total += Number(prog?.total_count || 0);
          } catch {
            // ignore per-habit failures
          }
        })
    );

    const pct = total > 0 ? (completed / total) * 100 : null;
    setAdherence({ pct, completed, total, streak: 0, daily: [] });
  } catch (e) {
    console.warn("Adherence load failed:", e?.message || e);
    setAdherence({ pct: null, completed: 0, total: 0, streak: 0, daily: [] });
  } finally {
    setAdherenceLoading(false);
  }
};

useEffect(() => {
  loadAdherence(adherenceDays);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [token, adherenceDays]);


  const me = authUser?.user_id;
  const isAdmin = !!authUser?.is_admin;

  // Role scope from backend (admin/trainer/self + allowed users list)
  const [scope, setScope] = useState("self"); // self | trainer | admin
  const [scopedUsers, setScopedUsers] = useState([]); // [{user_id,name,email,role}]

  // Admin fallback: normalize adminUsers list
  const normalizedAdminUsers = useMemo(() => {
    return (adminUsers || [])
      .map((u) => u?.auth_info || u?.user || u)
      .filter((u) => u && u.user_id);
  }, [adminUsers]);

  const selectableUsers = useMemo(() => {
    if (scope === "admin") return scopedUsers.length ? scopedUsers : normalizedAdminUsers;
    if (scope === "trainer") return scopedUsers;
    return [];
  }, [scope, scopedUsers, normalizedAdminUsers]);

  // Admin/Trainer view/create controls
  const [viewUser, setViewUser] = useState("me"); // "me" | "all" | user_id
  const [createFor, setCreateFor] = useState("me"); // "me" | user_id

  // Tabs
  const [tab, setTab] = useState("today"); // "today" | "manage"

  // Data
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(false);

  // Today view
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [statusByHabit, setStatusByHabit] = useState({});
  const [statusLoading, setStatusLoading] = useState(false);
  const [metricOptions, setMetricOptions] = useState({ domains: [], fields: {}, connected: {} });

  const loadMetricOptions = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/metric-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMetricOptions(data || { domains: [], fields: {}, connected: {} });
    } catch (e) {
      console.warn("Failed to load metric options:", e?.message || e);
    }
  };

  useEffect(() => {
    loadMetricOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const emptyDraft = () => ({
    habit_id: null,
    name: "",
    description: "",
    habit_type: "checkbox", // checkbox|metric
    frequency: "daily", // daily|weekly|monthly
    interval: 1,
    start_date: todayISO(),
    end_date: "",
    time_of_day: "09:00",
    timezone: "America/Los_Angeles",
    days_of_week: [0, 1, 2, 3, 4],
    day_of_month: 1,
    metric_rule: { domain: "food", field: "calories", operator: "lt", value: "" },
    metric_window: { mode: "today", days: 7, start_date: "", end_date: "" },
  });

  const [draft, setDraft] = useState(emptyDraft());
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  // Metric fields per domain
  const metricFields = useMemo(() => {
    const d = (draft.metric_rule?.domain || "food").toLowerCase();
    const fields = metricOptions.fields?.[d];
    return Array.isArray(fields) && fields.length ? fields : ["calories"];
  }, [draft.metric_rule?.domain, metricOptions]);

  useEffect(() => {
    if (draft.habit_type !== "metric") return;
    const f = draft.metric_rule?.field;
    if (!metricFields.includes(f)) {
      setDraft((p) => ({
        ...p,
        metric_rule: { ...p.metric_rule, field: metricFields[0] },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.metric_rule?.domain]);

  // ---------------- Role scope loader ----------------
  const loadScope = async () => {
    if (!token) return;

    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/scope`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        setScope(isAdmin ? "admin" : "self");
        setScopedUsers([]);
        setViewUser(isAdmin ? "all" : "me");
        setCreateFor("me");
        return;
      }

      const data = await res.json();
      const sc = data?.scope || (isAdmin ? "admin" : "self");
      setScope(sc);
      setScopedUsers(data?.users || []);

      if (sc === "admin") {
        setViewUser("all");
        setCreateFor("me");
      } else if (sc === "trainer") {
        setViewUser("all");
        setCreateFor("me");
      } else {
        setViewUser("me");
        setCreateFor("me");
      }
    } catch (e) {
      console.error("Failed to load habits scope:", e);
      setScope(isAdmin ? "admin" : "self");
      setScopedUsers([]);
      setViewUser(isAdmin ? "all" : "me");
      setCreateFor("me");
    }
  };

  useEffect(() => {
    loadScope();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ---------------- Habits list loader ----------------
  const loadHabits = async () => {
    if (!token) return [];
    setLoading(true);

    try {
      const params = new URLSearchParams();
      if (scope === "admin" || scope === "trainer") {
        if (viewUser === "me") params.set("user_id", "me");
        else if (viewUser === "all") params.set("user_id", "all");
        else if (viewUser) params.set("user_id", viewUser);
      }

      const url = `${BACKEND_URL}/api/habits${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load habits");
      }

      const data = await res.json();

      // ✅ filter out any broken docs lacking habit_id
      const list = (data.habits || []).filter((h) => h && h.habit_id);

      setHabits(list);

      // ✅ also prune statuses for habits no longer present
      setStatusByHabit((prev) => {
        const next = {};
        for (const h of list) {
          if (prev[h.habit_id]) next[h.habit_id] = prev[h.habit_id];
        }
        return next;
      });

      return list;
    } catch (e) {
      showToast?.("Error loading habits: " + (e?.message || String(e)), "error");
      setHabits([]);
      setStatusByHabit({});
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHabits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewUser, scope]);

  // ---------------- Today status loader ----------------
  const loadStatusesForHabits = async (habitList) => {
    if (!token) return;
    setStatusLoading(true);

    try {
      const safeList = (habitList || []).filter((h) => h && h.habit_id);

      const entries = await Promise.all(
        safeList.map(async (h) => {
          const url = `${BACKEND_URL}/api/habits/${h.habit_id}/status?date=${encodeURIComponent(
            selectedDate
          )}&_=${Date.now()}`;

          try {
            const res = await fetch(url, {
              headers: { Authorization: `Bearer ${token}` },
              cache: "no-store",
            });

            // ✅ if habit was deleted/broken, drop it locally so we never query it again
            if (res.status === 404) {
              setHabits((prev) => prev.filter((x) => x?.habit_id !== h.habit_id));
              return [h.habit_id, { deleted: true }];
            }

            if (!res.ok) return [h.habit_id, { error: true }];

            const data = await res.json();
            return [h.habit_id, data];
          } catch (err) {
            // ✅ network/CORS/proxy failures show up here as "Failed to fetch"
            return [h.habit_id, { error: true, network: true }];
          }
        })
      );

      const map = {};
      for (const [id, s] of entries) {
        if (!s?.deleted) map[id] = s;
      }

      // ✅ also ensure we only keep statuses for habits that still exist in current state
      setStatusByHabit((prev) => {
        const existingIds = new Set((safeList || []).map((h) => h.habit_id));
        const merged = { ...prev, ...map };
        const next = {};
        for (const id of Object.keys(merged)) {
          if (existingIds.has(id)) next[id] = merged[id];
        }
        return next;
      });
    } finally {
      setStatusLoading(false);
    }
  };

  const refreshToday = async () => {
    const list = await loadHabits();
    await loadStatusesForHabits(list);
  };

  // ✅ fetch statuses when entering today tab or date changes
  useEffect(() => {
    loadStatusesForHabits(habits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // ---------------- Payload builder ----------------
  const buildPayloadFromDraft = (d, targetUserId) => {
    const base = {
      ...(targetUserId ? { user_id: targetUserId } : {}),

      name: d.name,
      description: d.description,

      habit_type: d.habit_type,
      frequency: d.frequency,
      interval: safeNum(d.interval) || 1,

      start_date: d.start_date,
      end_date: d.end_date,

      time_of_day: d.time_of_day || "09:00",
      timezone: d.timezone || "America/Los_Angeles",

      days_of_week: d.frequency === "weekly" ? (d.days_of_week || []) : undefined,
      day_of_month: d.frequency === "monthly" ? safeNum(d.day_of_month) : undefined,

      requires_checkoff: d.habit_type === "checkbox",

      metric_rule:
        d.habit_type === "metric"
          ? {
              domain: d.metric_rule.domain,
              field: d.metric_rule.field,
              operator: d.metric_rule.operator,
              value: safeNum(d.metric_rule.value),
            }
          : undefined,
    };

    return compact(base);
  };

  const resetDraft = () => setDraft(emptyDraft());

  // ---------------- Create habit ----------------
  const createHabit = async (e) => {
    e.preventDefault();
    if (!token) return;

    let targetUserId = null;

    if (scope === "admin" || scope === "trainer") {
      const target = createFor || "me";
      if (target !== "me") {
        if (scope === "trainer") {
          const allowed = (scopedUsers || []).some((u) => u.user_id === target);
          if (!allowed) {
            showToast?.("You can only create habits for yourself or your assigned clients.", "error");
            return;
          }
        }
        targetUserId = target;
      }
    }

    if (!draft.name.trim()) return showToast?.("Please enter a habit name.", "error");
    if (draft.frequency === "weekly" && (!draft.days_of_week || draft.days_of_week.length === 0)) {
      return showToast?.("Please pick at least one day of the week.", "error");
    }
    if (draft.habit_type === "metric" && (draft.metric_rule.value === "" || draft.metric_rule.value === null)) {
      return showToast?.("Please enter a metric value.", "error");
    }

    const payload = buildPayloadFromDraft(draft, targetUserId);

    try {
      const res = await fetch(`${BACKEND_URL}/api/habits`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Create failed");
      }

      resetDraft();
      setShowCreate(false);
      await refreshToday();
      showToast?.("Habit created (and calendar events were generated)!");
    } catch (e2) {
      showToast?.("Error creating habit: " + (e2?.message || String(e2)), "error");
    }
  };

  // ---------------- Delete habit ----------------
  const deleteHabit = async (habitId) => {
    if (!window.confirm("Delete this habit? This will also remove its calendar events.")) return;

    // ✅ optimistic remove so status is never fetched for it again
    setHabits((prev) => prev.filter((h) => h?.habit_id !== habitId));
    setStatusByHabit((prev) => {
      const next = { ...prev };
      delete next[habitId];
      return next;
    });

    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/${habitId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Delete failed");
      }

      await refreshToday();
    } catch (e) {
      showToast?.("Error deleting habit: " + (e?.message || String(e)), "error");
      // restore truth from server
      await refreshToday();
    }
  };

  // ---------------- Edit modal ----------------
  const openEdit = (h) => {
    setDraft({
      habit_id: h.habit_id,
      name: h.name || "",
      description: h.description || "",
      habit_type: h.habit_type || "checkbox",
      frequency: h.frequency || "daily",
      interval: h.interval ?? 1,
      start_date: h.start_date || todayISO(),
      end_date: h.end_date || "",
      time_of_day: h.time_of_day || "09:00",
      timezone: h.timezone || "America/Los_Angeles",
      days_of_week: h.days_of_week || [0, 1, 2, 3, 4],
      day_of_month: h.day_of_month ?? 1,
      metric_rule: h.metric_rule
        ? {
            domain: h.metric_rule.domain || "food",
            field: h.metric_rule.field || "calories",
            operator: h.metric_rule.operator || "lt",
            value: String(h.metric_rule.value ?? ""),
          }
        : { domain: "food", field: "calories", operator: "lt", value: "" },
    });
    setShowEdit(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!draft.habit_id) return;

    if (!draft.name.trim()) return showToast?.("Please enter a habit name.", "error");
    if (draft.frequency === "weekly" && (!draft.days_of_week || draft.days_of_week.length === 0)) {
      return showToast?.("Please pick at least one day of the week.", "error");
    }
    if (draft.habit_type === "metric" && (draft.metric_rule.value === "" || draft.metric_rule.value === null)) {
      return showToast?.("Please enter a metric value.", "error");
    }

    const payload = buildPayloadFromDraft(draft, null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/${draft.habit_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Update failed");
      }

      setShowEdit(false);
      resetDraft();
      await refreshToday();
      showToast?.("Habit updated (calendar events were resynced)!");
    } catch (e2) {
      showToast?.("Error updating habit: " + (e2?.message || String(e2)), "error");
    }
  };

  // ---------------- Check-off ----------------
  const toggleComplete = async (habitId, checked) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/${habitId}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ habit_id: habitId, date: selectedDate, checked }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to update completion");
      }

      // ✅ refresh only statuses for current list (no stale ids)
      await loadStatusesForHabits(habits);
    } catch (e) {
      showToast?.("Error updating habit completion: " + (e?.message || String(e)), "error");
    }
  };

  const sortedHabits = useMemo(() => {
    const arr = [...habits];
    arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return arr;
  }, [habits]);
  return (
    <div className="habits-view">
      <div className="habits-toolbar">
        <div>
          <h1 className="heading-2" style={{ marginBottom: 6 }}>Habits</h1>
          <div className="body-medium" style={{ color: "var(--text-secondary)" }}>
            Repeating habits that can appear on your calendar and be checked off daily.
          </div>
          <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
            Role: <strong>{scope}</strong>
          </div>
        </div>

        <div className="habits-actions">
          <button
            className="btn-primary"
            type="button"
            onClick={() => {
              resetDraft();
              setShowCreate(true);
            }}
          >
            + Add Habit
          </button>
        </div>
      </div>

      {(scope === "admin" || scope === "trainer") && (
        <div className="form-section" style={{ marginBottom: 18 }}>
          <h3 className="heading-3" style={{ marginBottom: 12 }}>
            {scope === "admin" ? "Admin Controls" : "Trainer Controls"}
          </h3>

          <div className="form-row habits-two-col">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="body-medium">View habits for</label>
              <select className="form-input" value={viewUser} onChange={(e) => setViewUser(e.target.value)}>
                <option value="all">{scope === "admin" ? "All users" : "My clients + me"}</option>
                <option value="me">Me</option>
                {selectableUsers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name || u.email || u.user_id}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="body-medium">Create habits for</label>
              <select className="form-input" value={createFor} onChange={(e) => setCreateFor(e.target.value)}>
                <option value="me">Me</option>
                {selectableUsers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name || u.email || u.user_id}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      <div className="form-section" style={{ marginBottom: 18 }}>
  

      {/* TODAY */}
      <div className="form-section" style={{ marginBottom: 18 }}>
          <div className="habits-section-header">
            <h3 className="heading-3" style={{ margin: 0 }}>Today’s Habits</h3>

            <div className="habits-date-controls">
              <label className="body-small" style={{ color: "var(--text-secondary)" }}>Date</label>
              <input
                type="date"
                className="form-input"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{ width: "100%", maxWidth: 170 }}
              />
              <button className="btn-secondary" type="button" onClick={refreshToday} disabled={statusLoading || loading}>
                {statusLoading || loading ? "…" : "Refresh"}
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            {loading ? (
              <div className="body-medium">Loading…</div>
            ) : sortedHabits.length === 0 ? (
              <div className="body-small" style={{ color: "var(--text-muted)" }}>
                No habits yet. Click “Add Habit” to create one.
              </div>
            ) : (
              <div className="habits-list">
                {sortedHabits.map((h) => {
                  const s = statusByHabit[h.habit_id];
                  const completed = !!s?.completed;
                  const metricMet = s?.type === "metric" ? !!s?.metric_met : null;
                  const metricActual = s?.metric_actual;

                  return (
                    <div
                      key={h.habit_id}
                      className="habit-today-card"
                    >
                      <div className="habit-today-content">
                        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 800 }}>{h.name}</div>

                          {h.habit_type === "metric" ? (
                            metricMet === true ? <HabitBadge bg="#16a34a">MET</HabitBadge>
                              : metricMet === false ? <HabitBadge bg="#dc2626">NOT MET</HabitBadge>
                              : <HabitBadge bg="#6b7280">METRIC</HabitBadge>
                          ) : (
                            completed ? <HabitBadge bg="#16a34a">DONE</HabitBadge> : <HabitBadge bg="#6b7280">TODO</HabitBadge>
                          )}

                          <span className="body-small" style={{ color: "var(--text-muted)" }}>
                            {h.frequency} • {h.time_of_day || "09:00"}
                          </span>
                        </div>

                        {h.description ? (
                          <div className="body-small" style={{ marginTop: 8, color: "var(--text-secondary)" }}>
                            {h.description}
                          </div>
                        ) : null}

                        {h.habit_type === "metric" && h.metric_rule ? (
                          <div className="body-small" style={{ marginTop: 8, color: "var(--text-secondary)" }}>
                            Rule: {h.metric_rule.domain}.{h.metric_rule.field} {h.metric_rule.operator} {h.metric_rule.value}
                            {metricActual !== undefined && metricActual !== null ? (
                              <span style={{ marginLeft: 10, color: "var(--text-muted)" }}>
                                (actual: {metricActual})
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="habit-today-actions">
                        {h.habit_type === "checkbox" ? (
                          <button
                            type="button"
                            className={completed ? "btn-secondary" : "btn-primary"}
                            onClick={() => toggleComplete(h.habit_id, !completed)}
                          >
                            {completed ? "Undo" : "Check off"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadStatusesForHabits(habits)}
                            disabled={statusLoading}
                          >
                            {statusLoading ? "…" : "Recheck"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      
      {/* CREATE HABIT MODAL */}
      {showCreate && (
        <div className="modal-overlay" onMouseDown={() => setShowCreate(false)}>
          <div
            className="modal-content ct-form-modal habits-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(920px, 100%)", maxWidth: 920 }}
          >
            <div
              className="modal-header habits-modal-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="heading-4" style={{ margin: 0 }}>Create Habit</div>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 4 }}>
                  Creating will also generate calendar events.
                </div>
              </div>
              <button type="button" className="habits-modal-close" onClick={() => setShowCreate(false)} aria-label="Close create habit">✕</button>
            </div>

            <form className="habits-modal-form habits-modal-scroll" onSubmit={createHabit}>
              <div className="form-group">
                <label className="body-medium">Name</label>
                <input
                  className="form-input"
                  value={draft.name}
                  onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="body-medium">Description (optional)</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={draft.description}
                  onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="form-row habits-two-col">
                <div className="form-group">
                  <label className="body-medium">Type</label>
                  <select
                    className="form-input"
                    value={draft.habit_type}
                    onChange={(e) => setDraft((p) => ({ ...p, habit_type: e.target.value }))}
                  >
                    <option value="checkbox">Checkbox (check-off)</option>
                    <option value="metric">Metric (based on logs)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="body-medium">Frequency</label>
                  <select
                    className="form-input"
                    value={draft.frequency}
                    onChange={(e) => setDraft((p) => ({ ...p, frequency: e.target.value }))}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <div className="form-row habits-two-col">
                <div className="form-group">
                  <label className="body-medium">Interval</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    value={draft.interval}
                    onChange={(e) => setDraft((p) => ({ ...p, interval: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">Time of day</label>
                  <input
                    className="form-input"
                    type="time"
                    value={draft.time_of_day}
                    onChange={(e) => setDraft((p) => ({ ...p, time_of_day: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row habits-two-col">
                <div className="form-group">
                  <label className="body-medium">Start date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={draft.start_date}
                    onChange={(e) => setDraft((p) => ({ ...p, start_date: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">End date (optional)</label>
                  <input
                    className="form-input"
                    type="date"
                    value={draft.end_date || ""}
                    onChange={(e) => setDraft((p) => ({ ...p, end_date: e.target.value }))}
                  />
                </div>
              </div>

              {draft.frequency === "weekly" && (
                <div className="form-group">
                  <label className="body-medium">Days of week</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {weekLabels.map((label, idx) => {
                      const active = (draft.days_of_week || []).includes(idx);
                      return (
                        <button
                          key={label}
                          type="button"
                          className={active ? "btn-primary" : "btn-secondary"}
                          style={{
                            padding: "8px 12px",
                            fontSize: 12,
                            boxShadow: active ? "0 6px 18px rgba(0,0,0,0.12)" : "none",
                          }}
                          onClick={() =>
                            setDraft((p) => {
                              const cur = p.days_of_week || [];
                              const next = cur.includes(idx)
                                ? cur.filter((x) => x !== idx)
                                : [...cur, idx].sort((a, b) => a - b);
                              return { ...p, days_of_week: next };
                            })
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {draft.frequency === "monthly" && (
                <div className="form-group">
                  <label className="body-medium">Day of month</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="31"
                    value={draft.day_of_month}
                    onChange={(e) => setDraft((p) => ({ ...p, day_of_month: e.target.value }))}
                  />
                </div>
              )}

              {draft.habit_type === "metric" && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid var(--border-light)",
                    background: "var(--bg-secondary)",
                  }}
                >
                  <h4 className="heading-4" style={{ marginBottom: 12 }}>Metric Rule</h4>

                  <div className="form-row habits-two-col">
                    <div className="form-group">
                      <label className="body-medium">Domain</label>
                      <select
                        className="form-input"
                        value={draft.metric_rule.domain}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, domain: e.target.value },
                          }))
                        }
                      >
                        {(metricOptions.domains?.length ? metricOptions.domains : [
                          { value: "food", label: "Nutrition (App)" },
                          { value: "exercise", label: "Exercise (App)" },
                          { value: "weight", label: "Weight" },
                        ]).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>

                    </div>

                    <div className="form-group">
                      <label className="body-medium">Field</label>
                      <select
                        className="form-input"
                        value={draft.metric_rule.field}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, field: e.target.value },
                          }))
                        }
                      >
                        {metricFields.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row habits-three-col">
                    <div className="form-group">
                      <label className="body-medium">Operator</label>
                      <select
                        className="form-input"
                        value={draft.metric_rule.operator}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, operator: e.target.value },
                          }))
                        }
                      >
                        <option value="lt">Less than</option>
                        <option value="gt">Greater than</option>
                        <option value="eq">Equal to</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ gridColumn: "span 2" }}>
                      <label className="body-medium">Value</label>
                      <input
                        className="form-input"
                        type="number"
                        step="0.1"
                        value={draft.metric_rule.value}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, value: e.target.value },
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              
              <div className="habits-modal-footer">
                
                <button type="submit" className="btn-primary">
                  Create Habit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
          <div className="form-section">
            <h3 className="heading-3" style={{ marginBottom: 12 }}>
              Manage Habits {loading ? "(loading…)" : `(${habits.length})`}
            </h3>

            {habits.length === 0 && !loading ? (
              <div className="body-small" style={{ color: "var(--text-muted)" }}>
                No habits yet. Create one above.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {habits.map((h) => (
                  <div
                    key={h.habit_id}
                    style={{
                      background: "var(--bg-secondary)",
                      padding: 14,
                      borderRadius: 12,
                      border: "1px solid var(--border-light)",
                    }}
                  >
                    <div className="habit-manage-row">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800 }}>{h.name}</div>
                        <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 4 }}>
                          {h.habit_type} • {h.frequency} • {h.time_of_day || "09:00"} • start {h.start_date}
                          {h.end_date ? ` • end ${h.end_date}` : " • no end"}
                        </div>
                        {h.description ? (
                          <div className="body-small" style={{ marginTop: 8, color: "var(--text-secondary)" }}>
                            {h.description}
                          </div>
                        ) : null}
                        {h.habit_type === "metric" && h.metric_rule ? (
                          <div className="body-small" style={{ marginTop: 8, color: "var(--text-secondary)" }}>
                            Rule: {h.metric_rule.domain}.{h.metric_rule.field} {h.metric_rule.operator} {h.metric_rule.value}
                          </div>
                        ) : null}
                      </div>

                      <div className="habit-manage-actions">
                        <button className="btn-secondary" type="button" onClick={() => openEdit(h)}>
                          Edit
                        </button>
                        <button
                          className="btn-secondary"
                          type="button"
                          onClick={() => deleteHabit(h.habit_id)}
                          style={{ color: "#dc2626", borderColor: "#dc2626" }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h3 className="heading-3" style={{ margin: 0 }}>Habit Adherence</h3>

        <select
          className="form-input"
          style={{ width: 160 }}
          value={adherenceDays}
          onChange={(e) => setAdherenceDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginTop: 12 }}>
        <div className="card" style={{ padding: 16 }}>
          <div className="body-small" style={{ color: "var(--text-muted)" }}>Completion %</div>
          <div className="heading-3" style={{ marginTop: 6 }}>{adherence.pct == null ? "—" : `${Math.round(adherence.pct)}%`}</div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div className="body-small" style={{ color: "var(--text-muted)" }}>Completed</div>
          <div className="heading-3" style={{ marginTop: 6 }}>{adherence.completed}</div>
        </div>

        <div className="card" style={{ padding: 16 }}>
          <div className="body-small" style={{ color: "var(--text-muted)" }}>Scheduled</div>
          <div className="heading-3" style={{ marginTop: 6 }}>{adherence.total}</div>
        </div>
      </div>

      {adherenceLoading && (
        <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 10 }}>
          Loading adherence…
        </div>
      )}
    </div>
      
      {/* EDIT MODAL */}
      {showEdit && (
        <div className="modal-overlay" onMouseDown={() => setShowEdit(false)}>
          <div
            className="modal-content ct-form-modal habits-modal"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ width: "min(920px, 100%)", maxWidth: 920 }}
          >
            <div
              className="modal-header habits-modal-header"
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="heading-4" style={{ margin: 0 }}>Edit Habit</div>
                <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 4 }}>
                  Saving will also resync upcoming calendar events.
                </div>
              </div>
              <button type="button" className="habits-modal-close" onClick={() => setShowEdit(false)} aria-label="Close edit habit">✕</button>
            </div>

            <form className="habits-modal-form habits-modal-scroll" onSubmit={saveEdit}>
              <div className="form-group">
                <label className="body-medium">Name</label>
                <input
                  className="form-input"
                  value={draft.name}
                  onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label className="body-medium">Description (optional)</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={draft.description}
                  onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="form-row habits-two-col">
                <div className="form-group">
                  <label className="body-medium">Type</label>
                  <select
                    className="form-input"
                    value={draft.habit_type}
                    onChange={(e) => setDraft((p) => ({ ...p, habit_type: e.target.value }))}
                  >
                    <option value="checkbox">Checkbox (check-off)</option>
                    <option value="metric">Metric (based on logs)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="body-medium">Frequency</label>
                  <select
                    className="form-input"
                    value={draft.frequency}
                    onChange={(e) => setDraft((p) => ({ ...p, frequency: e.target.value }))}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>

              <div className="form-row habits-two-col">
                <div className="form-group">
                  <label className="body-medium">Interval</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    value={draft.interval}
                    onChange={(e) => setDraft((p) => ({ ...p, interval: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">Time of day</label>
                  <input
                    className="form-input"
                    type="time"
                    value={draft.time_of_day}
                    onChange={(e) => setDraft((p) => ({ ...p, time_of_day: e.target.value }))}
                  />
                </div>
              </div>

              <div className="form-row habits-two-col">
                <div className="form-group">
                  <label className="body-medium">Start date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={draft.start_date}
                    onChange={(e) => setDraft((p) => ({ ...p, start_date: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="body-medium">End date (optional)</label>
                  <input
                    className="form-input"
                    type="date"
                    value={draft.end_date || ""}
                    onChange={(e) => setDraft((p) => ({ ...p, end_date: e.target.value }))}
                  />
                </div>
              </div>

              {draft.frequency === "weekly" && (
                <div className="form-group">
                  <label className="body-medium">Days of week</label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {weekLabels.map((label, idx) => {
                      const active = (draft.days_of_week || []).includes(idx);
                      return (
                        <button
                          key={label}
                          type="button"
                          className={active ? "btn-primary" : "btn-secondary"}
                          style={{ padding: "8px 12px", fontSize: 12, boxShadow: active ? "0 6px 18px rgba(0,0,0,0.12)" : "none" }}
                          onClick={() =>
                            setDraft((p) => {
                              const cur = p.days_of_week || [];
                              const next = cur.includes(idx)
                                ? cur.filter((x) => x !== idx)
                                : [...cur, idx].sort((a, b) => a - b);
                              return { ...p, days_of_week: next };
                            })
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {draft.frequency === "monthly" && (
                <div className="form-group">
                  <label className="body-medium">Day of month</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="31"
                    value={draft.day_of_month}
                    onChange={(e) => setDraft((p) => ({ ...p, day_of_month: e.target.value }))}
                  />
                </div>
              )}

              {draft.habit_type === "metric" && (
                <div
                  style={{
                    marginTop: 16,
                    padding: 16,
                    borderRadius: 12,
                    border: "1px solid var(--border-light)",
                    background: "var(--bg-section)",
                  }}
                >
                  <h4 className="heading-4" style={{ marginBottom: 12 }}>Metric Rule</h4>

                  <div className="form-row habits-two-col">
                    <div className="form-group">
                      <label className="body-medium">Domain</label>
                      <select
                        className="form-input"
                        value={draft.metric_rule.domain}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, domain: e.target.value },
                          }))
                        }
                      >
                        {(metricOptions.domains?.length ? metricOptions.domains : [
                          { value: "food", label: "Nutrition (App)" },
                          { value: "exercise", label: "Exercise (App)" },
                          { value: "weight", label: "Weight" },
                        ]).map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="body-medium">Field</label>
                      <select
                        className="form-input"
                        value={draft.metric_rule.field}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, field: e.target.value },
                          }))
                        }
                      >
                        {metricFields.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row habits-three-col">
                    <div className="form-group">
                      <label className="body-medium">Operator</label>
                      <select
                        className="form-input"
                        value={draft.metric_rule.operator}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, operator: e.target.value },
                          }))
                        }
                      >
                        <option value="lt">Less than</option>
                        <option value="gt">Greater than</option>
                        <option value="eq">Equal to</option>
                      </select>
                    </div>

                    <div className="form-group" style={{ gridColumn: "span 2" }}>
                      <label className="body-medium">Value</label>
                      <input
                        className="form-input"
                        type="number"
                        step="0.1"
                        value={draft.metric_rule.value}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            metric_rule: { ...p.metric_rule, value: e.target.value },
                          }))
                        }
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              
              <div className="habits-modal-footer">
                <button type="submit" className="btn-primary">
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
